<template>
    <div class="step-content" :class="{ mobile: isMobile }">
        <!-- Operation Info -->
        <text-blocks-operation-info v-if="responseData" :params="operationInfoParams"/>
        <!-- Transfer Info -->
        <div
        v-for="(block, index) in transferData"
        :key="index"
        class="step-section">
            <infobox-generic v-if="block.showInfo" :label="block.infoBoxLabel"/>
            <!-- Divider -->
            <divider-generic
            v-if="!block.divider.hidden"
            :dividerSize="'medium'"
            :applyBorder="block.divider.applyBorder"
            :icon="block.divider.icon"
            :title="block.divider.title"/>
            <!-- Text Block -->
            <text-blocks-generic
            v-for="(params, index) in block.params.filter(obj => !obj.hidden)"
            class="step-text-block"
            :key="index"
            :params="params"
            :preventEmptyDefaultBehaviour="params.preventEmptyDefaultBehaviour"/>
            <!-- Create Beneficiary Button -->
            <button-tertiary
            v-if="block.hasButton && !beneficiaryAdded"
            :key="beneficiaryAdded"
            icon="add-user"
            class="create-beneficiary-button"
            :label="t('transfers.nacional.step3.saveBeneficiary')"
            @clicked="emit('showCreateBeneficiaryModal')"/>
            <!-- TODO PARA A FASE 2 -->
            <!-- Safe Beneficiary Switch -->
            <!-- <switch-text
            v-if="block.hasSwitch"
            :text="t('transfers.nacional.step3.safeBeneficiary')"
            :isActive="safeBeneficiary"
            @clicked="safeBeneficiary =$event">
                <buttons-quaternary
                icon="info"
                buttonStyle="round"
                @clicked="showInfoTooltip = true"/>
            </switch-text> -->
            <!-- Safe Beneficiary Alert Tooltip -->
            <!-- <div v-if="showInfoTooltip" v-click-outside="closeTooltip" class="tooltip-wrapper">
                <span>{{t('transfers.nacional.step3.alertModal.message')}}</span>
                <button-primary
                :label="t('transfers.nacional.step3.alertModal.button')"
                @clicked="closeTooltip"/>
            </div> -->
        </div>
        <div class="step-section">
            <!-- Divider -->
            <divider-generic class="last-divider" :dividerSize="'medium'"/>
            <div class="button-wrapper">
                <button-tertiary
                v-if="responseData.favoritable"
                class="favorite-button"
                icon="star"
                iconPosition="right"
                :label="favoriteButtonLabel"
                @clicked="emit('showFavoriteModal')"/>
                <button-primary
                icon="transfer"
                buttonSize="medium"
                iconPosition="right"
                :label="t('transfers.nacional.step3.newTransfer')"
                @clicked="emit('newTransfer')"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'
import TextBlocksOperationInfo from '@/components/wrappers/OperationInfo'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ButtonPrimary from '@/components/generic/buttons/Primary'
/* TODO Para a fase 2 */
// import SwitchText from '@/components/generic/switch/Text'
// import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'

/* Vue && Assets */
import { ref, computed } from 'vue'
import { accountFormatHelper, ibanFormat } from '@/assets/scripts/strings'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate, getTimeWithSeconds } from '@/assets/scripts/dateBuilders'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const {
    getAccountsBalances,
    getScheduleTypes,
    getTransfersCategoriesList,
    getTransfersMotivesList
} = mapGetters(moduleMapper.TRANSFERS_NACIONAL)

/** Props
@formData {Object} - Object with form data
@formTabsList {Array} - Tab List for Iban or Account Transfer
@frequencyTabsList {Object} - Frequency Tab list
@isFavorite {Boolean} Boolean that tells us if transfer is allready in favorites list
@beneficiaryAdded {Boolean} Boolean that tells us if beneficiary allready exists
@formTabsList {Array} - Tab List for Iban or Account Transfer
@responseData {Object} - Request Response
*/
const props = defineProps({
    formData: Object,
    formTabsList: Array,
    isFavorite: Boolean,
    beneficiaryAdded: Boolean,
    frequencyTabsList: Object,
    responseData: Array
})

const emit = defineEmits([
    'showFavoriteModal',
    'newTransfer',
    'showCreateBeneficiaryModal'
])

const safeBeneficiary = ref(false)
const showInfoTooltip = ref(false)

const favoriteButtonLabel = computed(() => {
    return props.isFavorite ? t('transfers.nacional.step3.removeFavorite') : t('transfers.nacional.step3.saveAsFavorite')
})

const transferData = computed(() => {
    const selectedTransfer = props.formTabsList.find(tab => tab.selected === true).id

    if (selectedTransfer === 'samebank') {
         return samebankData.value
    } else if (selectedTransfer === 'otherbank') {
        return otherbankData.value
    } else return naHoraData.value
})

const originAccount = computed(() => {
    const accountNumber = getAccountsBalances.value.find(account => account.id === props.formData.originAccount).accountNumber

    return accountFormatHelper(accountNumber)
})

const destinationAccount = computed(() => {
    const account = props.formData.genericDestination

    return account.length === 14 ? accountFormatHelper(account) : ibanFormat(account)
})

const frequencyValues = computed(() => {
    const frequencyCode = props.formData.frequencyCode
    const startDate = props.formData.startDate
    const endDate = props.formData.endDate
    const recurrence = props.frequencyTabsList[1].selected ? getScheduleTypes.value.find(obj => obj.code === frequencyCode).description : null
    const interval = props.formData.frequency <= 1 ? t('transfers.nacional.step2.day', { number: props.formData.frequency }) : t('transfers.nacional.step2.days', { number: props.formData.frequency })

    if (props.frequencyTabsList[0].selected) {
        return {
            frequencyType: t('transfers.nacional.step1.frequencyTabs.oneOffTransfer'),
            startDate: getDate(new Date())
        }
    } else {
        return {
            frequencyType: t('transfers.nacional.step1.frequencyTabs.recurringTransfer'),
            startDate: getDate(startDate),
            recurrence: recurrence,
            endDate: endDate ? getDate(endDate) : null,
            interval: interval
        }
    }
})

const samebankData = computed(() => {
    return [
        {
            divider: { icon: 'account-balance', title: t('transfers.nacional.step2.dividers.originAccount'), applyBorder: true },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.account'),
                    content: originAccount.value
                }
            ]
        },
        {
            divider: { icon: 'group', title: t('transfers.nacional.step2.dividers.beneficiary'), applyBorder: true },
            hasSwitch: props.responseData.alreadyBeneficiary, // TODO Falta segunda validação para saber se ja é seguro
            hasButton: !props.responseData.alreadyBeneficiary,
            params: [
                {
                    label: t('transfers.nacional.genericLabels.genericDestination'),
                    content: destinationAccount.value
                },
                {
                    label: t('transfers.nacional.genericLabels.beneficaryName'),
                    content: props.formData.beneficiaryName
                }

            ]
        },
        {
            divider: { icon: 'bills', title: t('transfers.nacional.step2.dividers.amount') },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.amountTransfered'),
                    content: amountFormat(props.formData.amount, props.formData.accountCurrency)
                }
            ]
        },
        {
            divider: { icon: 'overview', title: t('transfers.nacional.step2.dividers.frequency'), applyBorder: true },
            params: props.frequencyTabsList[0].selected
            ? [
                {
                    label: t('transfers.nacional.genericLabels.typeOfPeriodicity'),
                    content: frequencyValues.value.frequencyType,
                    classes: 'capitalize'
                },
                {
                    label: t('transfers.nacional.genericLabels.transferDate'),
                    content: frequencyValues.value.startDate,
                    classes: 'capitalize'
                }
            ]
            : [
                {
                    label: t('transfers.nacional.genericLabels.typeOfPeriodicity'),
                    content: frequencyValues.value.frequencyType,
                    classes: 'capitalize'
                },
                {
                    label: t('transfers.nacional.genericLabels.startDate'),
                    content: frequencyValues.value.startDate,
                    classes: 'capitalize'
                },
                {
                    label: t('transfers.nacional.genericLabels.recurrence'),
                    content: frequencyValues.value.recurrence
                },
                {
                    label: t('transfers.nacional.genericLabels.interval'),
                    content: frequencyValues.value.interval,
                    hidden: props.formData.frequencyCode !== 'VARIAVEL'
                },
                {
                    label: t('transfers.nacional.genericLabels.endDate'),
                    content: frequencyValues.value.endDate,
                    classes: 'capitalize',
                    hidden: props.frequencyTabsList[1].selected && !props.formData.endDateSelected
                },
                {
                    label: t('transfers.nacional.step2.untilFurtherNotice'),
                    content: null,
                    preventEmptyDefaultBehaviour: true,
                    hidden: props.frequencyTabsList[1].selected && props.formData.endDateSelected
                }
            ]
        },
        {
            divider: {
                icon: 'tag',
                title: t('transfers.nacional.step2.dividers.organization'),
                applyBorder: true,
                hidden: !props.formData.senderDescription.length && !props.formData.receiverDescription.length
            },
            params: [
            {
					label: t('transfers.nacional.genericLabels.senderDescription'),
					content: props.formData.senderDescription || '-',
                    hidden: !props.formData.senderDescription.length
				},
                {
					label: t('transfers.nacional.genericLabels.receiverDescription'),
					content: props.formData.receiverDescription || '-',
                    hidden: !props.formData.receiverDescription.length
				}
            ]
        }
    ]
})

const otherbankData = computed(() => {
    return [
        {
            divider: { icon: 'account-balance', title: t('transfers.nacional.step2.dividers.originAccount'), applyBorder: true },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.account'),
                    content: originAccount.value
                }
            ]
        },
        {
            divider: { icon: 'group', title: t('transfers.nacional.step2.dividers.beneficiary'), applyBorder: true },
            hasSwitch: props.responseData.alreadyBeneficiary,
            hasButton: !props.responseData.alreadyBeneficiary,
            params: [
                {
                    label: t('transfers.nacional.genericLabels.iban'),
                    content: destinationAccount.value
                },
                {
                    label: t('transfers.nacional.genericLabels.beneficaryName'),
                    content: props.formData.beneficiaryName
                },
                {
                    label: t('transfers.nacional.genericLabels.address'),
                    content: props.formData.address
                }

            ]
        },
        {
            divider: { icon: 'bills', title: t('transfers.nacional.step2.dividers.amount') },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.amountTransfered'),
                    content: amountFormat(props.formData.amount, props.formData.accountCurrency)
                }
            ]
        },
        {
            divider: { icon: 'overview', title: t('transfers.nacional.step2.dividers.frequency'), applyBorder: true },
            params: props.formData.urgentTransfer ? [
                    {
                        label: t('transfers.nacional.genericLabels.typeOfPeriodicity'),
                        content: t('transfers.nacional.step2.oneOff')
                    },
                    {
                        label: t('transfers.nacional.genericLabels.urgentTransfer'),
                        content: t('transfers.nacional.step2.yes')
                    },
                    {
                        label: t('transfers.nacional.genericLabels.transferDate'),
                        content: frequencyValues.value.startDate,
                        classes: 'capitalize'
                    }
                ]
                : props.frequencyTabsList[0].selected ? [
                    {
                        label: t('transfers.nacional.genericLabels.typeOfPeriodicity'),
                        content: frequencyValues.value.frequencyType,
                        classes: 'capitalize'
                    },
                    {
                        label: t('transfers.nacional.genericLabels.transferDate'),
                        content: frequencyValues.value.startDate,
                        classes: 'capitalize'
                    }
                ]
                : [
                    {
                        label: t('transfers.nacional.genericLabels.typeOfPeriodicity'),
                        content: frequencyValues.value.frequencyType,
                        classes: 'capitalize'
                    },
                    {
                        label: t('transfers.nacional.genericLabels.startDate'),
                        content: frequencyValues.value.startDate,
                        classes: 'capitalize'
                    },
                    {
                        label: t('transfers.nacional.genericLabels.recurrence'),
                        content: frequencyValues.value.recurrence
                    },
                    {
                        label: t('transfers.nacional.genericLabels.interval'),
                        content: frequencyValues.value.interval,
                        hidden: props.formData.frequencyCode !== 'VARIAVEL'
                    },
                    {
                        label: t('transfers.nacional.genericLabels.endDate'),
                        content: frequencyValues.value.endDate,
                        classes: 'capitalize',
                        hidden: props.frequencyTabsList[1].selected && !props.formData.endDateSelected
                    },
                    {
                        label: t('transfers.nacional.step2.untilFurtherNotice'),
                        content: null,
                        preventEmptyDefaultBehaviour: true,
                        hidden: props.frequencyTabsList[1].selected && props.formData.endDateSelected
                    }
                ]
        },
        {
            divider: { icon: 'tag', title: t('transfers.nacional.step2.dividers.organization'), applyBorder: true },
            params: [
                {
					label: t('transfers.nacional.genericLabels.operationDescription'),
					content: props.formData.senderDescription || '-'
				},
                {
					label: t('transfers.nacional.genericLabels.category'),
					content: getTransfersCategoriesList.value.find(category => category.id === props.formData.category).description
				},
                {
					label: t('transfers.nacional.genericLabels.motive'),
					content: getTransfersMotivesList.value.find(reason => reason.id === props.formData.motive).description
				}
            ]
        }
    ]
})

const naHoraData = computed(() => {
    return [
        {
            divider: { icon: 'account-balance', title: t('transfers.nacional.step2.dividers.originAccount'), applyBorder: true },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.account'),
                    content: originAccount.value
                }
            ]
        },
        {
            divider: { icon: 'group', title: t('transfers.nacional.step2.dividers.beneficiary'), applyBorder: true },
            hasSwitch: props.responseData.alreadyBeneficiary,
            hasButton: !props.responseData.alreadyBeneficiary,
            params: [
                {
                    label: t('transfers.nacional.genericLabels.phoneNumber'),
                    content: props.formData.genericDestination.replace(/(?=(?:.{3})*$)/g, ' ')
                },
                {
                    label: t('transfers.nacional.genericLabels.beneficaryName'),
                    content: props.formData.beneficiaryName
                }

            ]
        },
        {
            divider: { icon: 'bills', title: t('transfers.nacional.step2.dividers.amount') },
            params: [
                {
                    label: t('transfers.nacional.genericLabels.amountTransfered'),
                    content: amountFormat(props.formData.amount, props.formData.accountCurrency)
                }
            ]
        },
        {
            divider: {
                icon: 'tag',
                title: t('transfers.nacional.step2.dividers.organization'),
                applyBorder: true,
                hidden: !props.formData.operationDescription.length
            },
            params: [
                {
					label: t('transfers.nacional.genericLabels.operationDescription'),
					content: props.formData.operationDescription || '-',
                    hidden: !props.formData.operationDescription.length
				}
            ]
        }
    ]
})

/* Operation Info */
const operationInfoParams = computed(() => {
	return {
		operationId: props.responseData.operationId,
		operationDate: props.responseData.creationDate,
		errorLevel: props.responseData.errorLevel,
		code: props.responseData.code
	}
})

const closeTooltip = () => {
    showInfoTooltip.value = false
}

</script>

<style lang="scss" scoped>
.step-content {
    width: 100%;
    margin-top: 24px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    .step-section {
        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;

        :deep(.button-quaternary) {
            width: 16px;
            height: 16px;

            position: relative;
        }

        .tooltip-wrapper {
            width: 320px;
            padding: 16px;
            gap: 16px;

            display: flex;
            flex-direction: column;
            position: absolute;

            border-radius: 8px;
            background-color: var(--secondary-light-4);
            box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.16);

            color: var(--secondary-dark-4);
            font-size: 14px;
            font-weight: 400;
            line-height: 18px;
            word-wrap: break-word;
        }

        .last-divider {
            padding-top: unset;
        }

        .button-wrapper {
            width: 100%;

            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 16px;
        }
    }

    &.mobile {
        :deep(.button-tertiary) {
            margin-bottom: 16px;
        }

        .step-section {
            padding: 16px 16px 24px 16px;
            border-radius: 12px;

            background-color: var(--white);
            box-shadow: 0 3px 6px 1px rgba(0, 50, 90, 0.16);

            &:not(:last-of-type) {
                margin-bottom: 16px;
            }
        }
    }

}
</style>
